import { Sparkles } from 'lucide-react';
import { ImageBackdropLayers, cn } from '@/components/help-center/latest-power-shared';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import PowerMentionText, { PowerMentionHtml } from '@/components/help-center/powers/PowerMentionText';
import { isLoreCuriosityColorfulIcon, LORE_CURIOSITY_ICON_TINT } from '@/lib/lore-curiosity-icons';
import { isLibraryIconRef, libraryIconSlug } from '@/lib/icon-library';
import { Icon } from '@/components/icons/Icon';
import { sanitizeCmsHtml, looksLikeHtml } from '@/lib/cms-html';
import {
  getPowerLoreTheme,
  powerLoreHasRenderableContent,
  resolvePowerLore,
  type PowerLore,
  type PowerLoreChapter,
  type PowerLoreCuriosity,
  type PowerLoreSource,
  type PowerLoreThemeStyle,
} from '@/lib/power-lore';

function LoreRichText({
  text,
  className,
  theme,
}: {
  text: string;
  className?: string;
  theme: PowerLoreThemeStyle;
}) {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (looksLikeHtml(trimmed)) {
    return (
      <PowerMentionHtml
        html={sanitizeCmsHtml(trimmed)}
        mentionClassName={theme.mention}
        className={cn(
          'font-inter text-base leading-relaxed text-blue-100/75 [&_a]:underline-offset-2 [&_p]:my-2',
          theme.richLink,
          className
        )}
      />
    );
  }
  return (
    <p className={cn('font-inter text-base leading-relaxed text-blue-100/75', className)}>
      <PowerMentionText text={trimmed} as="span" mentionClassName={theme.mention} />
    </p>
  );
}

function LoreChapterBlock({
  chapter,
  index,
  theme,
}: {
  chapter: PowerLoreChapter;
  index: number;
  theme: PowerLoreThemeStyle;
}) {
  const label = chapter.label.trim();
  const title = chapter.title.trim();
  const body = chapter.body.trim();
  if (!title && !body) return null;

  return (
    <li className="relative pl-10 sm:pl-12">
      <span
        className="absolute left-[11px] top-2 z-10 flex h-3.5 w-3.5 -translate-x-1/2 items-center justify-center sm:left-[15px]"
        aria-hidden
      >
        <span className={cn('absolute h-3.5 w-3.5 rounded-full', theme.dot)} />
        <span className={cn('absolute h-7 w-7 animate-pulse rounded-full', theme.dotHalo)} />
      </span>

      <article className="pb-16 last:pb-0">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span
            className={cn(
              'font-mono text-[10px] font-semibold tracking-[0.2em]',
              theme.chapterMeta
            )}
          >
            CAPÍTULO {index + 1}
          </span>
          {label ? (
            <span
              className={cn(
                'rounded-md border px-2 py-0.5 font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]',
                theme.badge
              )}
            >
              {label}
            </span>
          ) : null}
        </div>
        {title ? (
          <h3 className="mb-3 font-poppins text-lg font-bold tracking-tight text-white sm:text-xl">
            {title}
          </h3>
        ) : null}
        <LoreRichText text={body} theme={theme} />
      </article>
    </li>
  );
}

function isLocalIconRef(value: string): boolean {
  const v = value.trim();
  if (isLibraryIconRef(v)) return true;
  return v.startsWith('/') || v.startsWith('icons/') || /\.svg$/i.test(v);
}

function resolveLocalIconSrc(value: string): string {
  const v = value.trim();
  if (v.startsWith('/')) return v;
  if (v.startsWith('icons/')) return `/${v}`;
  if (/\.svg$/i.test(v)) return `/icons/${v.replace(/^\/+/, '')}`;
  return v;
}

function LoreCuriosityLibraryIcon({
  iconRef,
  tintColor,
  className = 'h-7 w-7',
}: {
  iconRef: string;
  tintColor: string;
  className?: string;
}) {
  const src = resolveLocalIconSrc(iconRef);
  if (isLoreCuriosityColorfulIcon(src)) {
    return <img src={src} alt="" className={cn(className, 'object-contain')} draggable={false} />;
  }
  return (
    <span style={{ color: tintColor }} className="inline-flex">
      <MaskedSvgIcon src={src} className={className} />
    </span>
  );
}

function LoreCuriosityCard({
  item,
}: {
  item: PowerLoreCuriosity;
}) {
  const title = item.title.trim() || 'Você sabia?';
  const body = item.body.trim();
  if (!body && !item.title.trim()) return null;

  const iconRef = item.smiley.trim();
  const localIcon = iconRef && isLocalIconRef(iconRef);

  // Links nas curiosidades: sempre âmbar (modelo Épico), independente do tema da história.
  const curiosityTheme = getPowerLoreTheme('epic');

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-amber-400/25',
        'bg-gradient-to-br from-amber-400/[0.12] via-[#030023]/40 to-sky-500/[0.08]',
        'p-4 shadow-[0_0_24px_-8px_rgba(251,191,36,0.35)] ring-1 ring-amber-300/10 sm:p-5'
      )}
    >
      <div
        className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-300/10 blur-2xl"
        aria-hidden
      />
      <div className="relative flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-400/15">
          {localIcon ? (
            isLibraryIconRef(iconRef) && libraryIconSlug(iconRef) ? (
              <Icon name={libraryIconSlug(iconRef)!} className="h-7 w-7 text-amber-200" />
            ) : (
              <LoreCuriosityLibraryIcon iconRef={iconRef} tintColor={LORE_CURIOSITY_ICON_TINT} />
            )
          ) : iconRef ? (
            <PowerSectionSmileyIcon code={iconRef} size="md" />
          ) : (
            <Sparkles className="h-5 w-5 text-amber-300" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-1.5 font-poppins text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200/90">
            {title}
          </p>
          <LoreRichText
            text={body}
            theme={curiosityTheme}
            className="text-sm text-amber-50/80 [&_strong]:text-amber-50"
          />
        </div>
      </div>
    </div>
  );
}

function LoreStarField({ className }: { className?: string }) {
  return (
    <div className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)} aria-hidden>
      <span className="absolute left-[12%] top-[18%] h-0.5 w-0.5 rounded-full bg-sky-200/70 shadow-[0_0_6px_1px_rgba(125,211,252,0.8)]" />
      <span className="absolute left-[78%] top-[28%] h-1 w-1 rounded-full bg-white/50 shadow-[0_0_8px_1px_rgba(255,255,255,0.45)]" />
      <span className="absolute left-[22%] top-[72%] h-0.5 w-0.5 rounded-full bg-cyan-200/60" />
      <span className="absolute left-[88%] top-[62%] h-0.5 w-0.5 rounded-full bg-sky-300/50" />
      <span className="absolute left-[48%] top-[12%] h-px w-px rounded-full bg-white/80" />
      <span className="absolute left-[62%] top-[80%] h-0.5 w-0.5 rounded-full bg-blue-200/40" />
    </div>
  );
}

type PowerDetailLoreSectionProps = {
  power: PowerLoreSource;
  /** Lore já resolvida (ex.: preview do editor). Se omitida, resolve a partir do power. */
  lore?: PowerLore | null;
  /** Modo compacto para preview no CMS. */
  compact?: boolean;
  className?: string;
};

export default function PowerDetailLoreSection({
  power,
  lore: loreProp,
  compact = false,
  className,
}: PowerDetailLoreSectionProps) {
  const lore = loreProp != null ? loreProp : resolvePowerLore(power).lore;

  if (!powerLoreHasRenderableContent(lore)) return null;

  const theme = getPowerLoreTheme(lore!.theme);
  const smiley =
    lore!.smiley.trim() ||
    power.smilies?.[0]?.replace(/[()]/g, '').trim() ||
    power.slug ||
    'smile';
  const subtitle = lore!.subtitle.trim() || 'Nos arquivos do xat';
  const title = lore!.title.trim() || 'A história por trás do Power';
  const intro = lore!.intro.trim();
  const chapters = lore!.chapters.filter((c) => c.title.trim() || c.body.trim());
  const curiosities = lore!.curiosities.filter((c) => c.body.trim() || c.title.trim());

  return (
    <section
      className={cn(
        'relative overflow-hidden border-y border-blue-900/50',
        compact ? 'py-8' : 'py-16 sm:py-20',
        className
      )}
      aria-label="Lore e história do Power"
    >
      <div className="pointer-events-none absolute inset-0 bg-[#020018]" aria-hidden />
      <ImageBackdropLayers className="pointer-events-none absolute inset-0 opacity-80" />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#000b6a]/50 via-[#030023]/70 to-[#020018]"
        aria-hidden
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent',
          theme.topHairline
        )}
        aria-hidden
      />
      <LoreStarField />

      <div className={cn('relative z-10 mx-auto max-w-4xl px-6')}>
        <div
          className={cn(
            'relative overflow-hidden rounded-3xl border bg-[#030023]/55 backdrop-blur-sm',
            'ring-1 p-6 sm:p-10 lg:p-12',
            theme.cardBorder,
            theme.cardRing,
            theme.cardShadow
          )}
        >
          <div
            className={cn(
              'pointer-events-none absolute -left-20 top-10 h-56 w-56 rounded-full blur-3xl',
              theme.orbPrimary
            )}
            aria-hidden
          />
          <div
            className={cn(
              'pointer-events-none absolute -right-16 bottom-0 h-48 w-48 rounded-full blur-3xl',
              theme.orbSecondary
            )}
            aria-hidden
          />

          <header className="relative mb-10 flex flex-col items-center text-center sm:mb-12">
            <div className="relative mb-5 flex w-full justify-center">
              <div className="relative flex items-center justify-center">
                <div
                  className={cn(
                    'pointer-events-none absolute inset-0 scale-150 rounded-full blur-2xl',
                    theme.smileyGlow
                  )}
                  aria-hidden
                />
                <div
                  className={cn(
                    'relative mx-auto flex items-center justify-center rounded-full border',
                    theme.smileyFrame,
                    'h-[5.25rem] w-[5.25rem] p-2 sm:h-24 sm:w-24 sm:p-2.5'
                  )}
                >
                  <span className="flex h-full w-full items-center justify-center">
                    <PowerSectionSmileyIcon code={smiley} size="hero" />
                  </span>
                </div>
              </div>
            </div>

            <p
              className="mb-2 font-poppins text-[11px] font-semibold uppercase tracking-[0.28em]"
              style={{ color: theme.swatch }}
            >
              {subtitle}
            </p>
            <h2
              className={cn(
                'max-w-2xl font-poppins font-bold tracking-tight text-white',
                'text-2xl sm:text-3xl lg:text-[2rem]'
              )}
            >
              {title}
            </h2>
            {intro ? (
              <div className="mt-5 max-w-2xl text-left sm:text-center">
                <LoreRichText
                  text={intro}
                  theme={theme}
                  className="sm:[&_p]:text-center"
                />
              </div>
            ) : null}
          </header>

          {chapters.length > 0 ? (
            <div className="relative mx-auto max-w-2xl">
              <div
                className={cn(
                  'absolute bottom-2 left-[11px] top-2 w-px -translate-x-1/2 bg-gradient-to-b sm:left-[15px]',
                  theme.timeline
                )}
                aria-hidden
              />
              <ol className="relative list-none p-0">
                {chapters.map((chapter, index) => (
                  <LoreChapterBlock
                    key={chapter.id}
                    chapter={chapter}
                    index={index}
                    theme={theme}
                  />
                ))}
              </ol>
            </div>
          ) : null}

          {curiosities.length > 0 ? (
            <div className="mx-auto max-w-2xl">
              {chapters.length > 0 ? (
                <PortalCrystalSeparator
                  className="mb-10 mt-12"
                  accentRgb={theme.accentRgb}
                />
              ) : null}
              <div className="grid gap-4">
                {curiosities.map((item) => (
                  <LoreCuriosityCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
